/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import * as React from 'react';
import { IResourceId, IResourceStore, IWebtopResourceModel } from 'apmui/resource';
import WebtopResourceView from 'apmui/resource/WebtopResourceView';
import RESOURCE from 'apmui/resource/ResourceType';
import bind from 'apmui/decorators/bind';
import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import * as Folder from './Folder';
import WebtopFolderModel from './Model';
import ModalWindow from 'apmui/components/ModalWindow';

const app = App.get();

interface IWebtopFolderProps {
    folder: WebtopFolderModel;
    modal?: boolean;
    parentId?: string;
    resources: IResourceStore;
    onClose?: () => void;
}

interface IWebtopFolderState {
    folder: WebtopFolderModel;
}

interface IWebtopFolderLinkProps {
    value: WebtopFolderModel;
    onLaunch: (resource: IWebtopResourceModel) => void;
}

class WebtopFolderLink extends React.Component<IWebtopFolderLinkProps> {
    @bind
    onClick() {
        this.props.onLaunch(this.props.value);
    }

    render() {
        return (
            <span className="webtop-folder-link">
                <button className="apmui-button-inline apmui-link" onClick={this.onClick}>
                    {this.props.value.caption}
                </button>
            </span>
        );
    }
}

class WebtopFolderView extends React.Component<IWebtopFolderProps, IWebtopFolderState> {
    resourceRef: HTMLElement | null;

    state: IWebtopFolderState = {
        folder: this.props.folder,
    };

    @bind
    setResourceRef(element: HTMLElement | null) {
        this.resourceRef = element;
    }

    @bind
    onClose() {
        if (this.props.onClose) {
            this.props.onClose();
        } else {
            app.dispatch(EventTags.RESOURCE_CLOSED, this.props.folder);
        }
    }

    @bind
    onResourceLaunch(resource: IWebtopResourceModel) {
        if (resource.type === RESOURCE.WEBTOP_FOLDER) {
            this.setState({ folder: resource });
            return true;
        }
        return false;
    }

    renderResources() {
        const resources = this.props.resources;
        return this.state.folder.assignedResources
            .filter((e) => resources[e.type] && resources[e.type][e.id])
            .map((e: IResourceId): IWebtopResourceModel => resources[e.type][e.id])
            .sort((a, b) => {
                if (a.type !== b.type) {
                    if (a.type === RESOURCE.WEBTOP_FOLDER) {
                        return -1;
                    }
                    if (b.type === RESOURCE.WEBTOP_FOLDER) {
                        return 1;
                    }
                }
                return a.caption.toLowerCase().localeCompare(b.caption.toLowerCase());
            })
            .map((resource) => (
                <WebtopResourceView
                    key={resource.type + ':' + resource.id}
                    onLaunch={this.onResourceLaunch}
                    resource={resource}
                />
            ));
    }

    renderAddressLine() {
        const elements = [];
        let folder: WebtopFolderModel | undefined = Folder.getParentFolder(this.state.folder, this.props.resources);

        while (folder) {
            elements.unshift(<WebtopFolderLink value={folder} onLaunch={this.onResourceLaunch} />);
            folder = Folder.getParentFolder(folder, this.props.resources);
        }

        return (
            <div className="folder-browser-address">
                <img src={this.state.folder.icon} alt="" />
                {elements}
                <span className="webtop-folder-link">{this.state.folder.caption}</span>
            </div>
        );
    }

    render() {
        const addressLine = this.renderAddressLine();
        const resources = this.renderResources();
        const Container = this.props.modal ? ModalWindow : 'div';
        const containerId = this.props.parentId
            ? `webtop-folder-browser:${this.props.parentId}`
            : 'webtop-folder-browser';
        return (
            <Container
                className="apmui-webtop-folder-browser"
                id={containerId}
                onClose={this.props.modal ? this.onClose : undefined}>
                {addressLine}
                {!this.props.modal && <button className="apmui-modal--close" onClick={this.onClose} />}
                <div className="apmui-webtop-section" id={this.state.folder.id}>
                    <div className="apmui-webtop-section--body" ref={this.setResourceRef}>
                        {resources}
                    </div>
                </div>
            </Container>
        );
    }

    setFocusToFirstResource() {
        if (this.resourceRef) {
            // set focus to 1st resource in this folder
            const resource = this.resourceRef.querySelector('.apmui-webtop-resource');
            if (resource) {
                (resource as HTMLElement).focus({ preventScroll: false });
            }
        }
    }

    componentDidUpdate(_: any, prevState: IWebtopFolderState) {
        if (prevState.folder !== this.state.folder) {
            // folder changed: set focus to 1st resource in new folder
            this.setFocusToFirstResource();
        }
    }

    componentDidMount() {
        this.setFocusToFirstResource();
    }
}

export default WebtopFolderView;
